import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Coffee, Users, Briefcase, BarChart2, ArrowUpRight, Eye, MousePointerClick } from 'lucide-react'
import { apiFetch } from '../../config/api'

function formatCount(value: number | null) {
  if (value == null) return '—'
  return value.toLocaleString('de-DE')
}

function greetingForHour(hour: number) {
  if (hour < 12) return 'Guten Morgen'
  if (hour < 18) return 'Guten Tag'
  return 'Guten Abend'
}

export default function DashboardHome() {
  const [eventCount, setEventCount] = useState<number | null>(null)
  const [userCount, setUserCount] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState<{ name?: string } | null>(null)
  const [serverUptime, setServerUptime] = useState<string | null>(null)
  const [serverResponseMs, setServerResponseMs] = useState<number | null>(null)
  const [visitorsToday, setVisitorsToday] = useState<number | null>(null)
  const [pageViews7d, setPageViews7d] = useState<number | null>(null)
  const [serverOnline, setServerOnline] = useState<boolean | null>(null)

  useEffect(() => {
    const userStr = localStorage.getItem('dashboard_user')
    if (!userStr) return
    try {
      setCurrentUser(JSON.parse(userStr) as { name?: string })
    } catch (error) {
      console.error('Error parsing user data:', error)
    }
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('dashboard_token') || ''

    const fetchStats = async () => {
      try {
        let nextUserCount = 0
        try {
          const usersResponse = await apiFetch('/users', {
            headers: { Authorization: token },
          })
          const usersData = await usersResponse.json()
          nextUserCount = usersData.success && usersData.users ? usersData.users.length : 0
        } catch (error) {
          console.error('Error fetching users:', error)
        }

        let nextEventCount = 0
        try {
          const eventsResponse = await apiFetch('/events', {
            headers: { Authorization: token },
          })
          const eventsData = await eventsResponse.json()
          if (eventsData.success && eventsData.data) {
            nextEventCount = Array.isArray(eventsData.data)
              ? eventsData.data.filter((event: { status?: string }) => event.status === 'Aktiv').length
              : 0
          }
        } catch (error) {
          console.error('Error fetching events:', error)
        }

        setUserCount(nextUserCount)
        setEventCount(nextEventCount)
      } catch (error) {
        console.error('Error fetching stats:', error)
      } finally {
        setLoading(false)
      }
    }

    const fetchServerAndAnalytics = async () => {
      if (!token) return
      try {
        const start = performance.now()
        const res = await apiFetch('/stats', { headers: { Authorization: token } })
        setServerResponseMs(Math.round(performance.now() - start))
        if (!res.ok) {
          setServerOnline(false)
          return
        }
        const data = await res.json()
        if (data.success) {
          setServerOnline(data.server?.online ?? true)
          setServerUptime(data.server?.uptime ?? null)
          setVisitorsToday(data.visitors_today ?? 0)
          setPageViews7d(data.page_views_7d ?? 0)
        }
      } catch {
        setServerOnline(false)
      }
    }

    fetchStats()
    fetchServerAndAnalytics()
  }, [])

  const name = currentUser?.name || 'Admin'
  const cards = [
    {
      label: 'Aktive Events',
      value: formatCount(loading ? null : eventCount),
      hint: 'Veröffentlichte Veranstaltungen',
      loading,
      icon: Calendar,
    },
    {
      label: 'Benutzer',
      value: formatCount(loading ? null : userCount),
      hint: 'Konten mit Dashboard-Zugang',
      loading,
      icon: Users,
    },
    {
      label: 'Besucher heute',
      value: formatCount(visitorsToday),
      hint: 'Aufrufe öffentlicher Seiten',
      loading: visitorsToday == null && serverOnline !== false,
      icon: Eye,
    },
    {
      label: 'Seitenaufrufe',
      value: formatCount(pageViews7d),
      hint: 'Summe der letzten 7 Tage',
      loading: pageViews7d == null && serverOnline !== false,
      icon: MousePointerClick,
    },
  ]

  const shortcuts = [
    { to: '/dashboard/events', icon: Calendar, label: 'Events verwalten', detail: 'Termine anlegen und bearbeiten' },
    { to: '/dashboard/brunches', icon: Coffee, label: 'Brunch verwalten', detail: 'Angebote und Termine' },
    { to: '/dashboard/bewerbung', icon: Briefcase, label: 'Bewerbungen', detail: 'Stellenanzeigen pflegen' },
    { to: '/dashboard/users', icon: Users, label: 'Benutzer', detail: 'Zugänge verwalten' },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-950">Willkommen zurück</h1>
          <p className="mt-1 text-sm text-zinc-500">
            {greetingForHour(new Date().getHours())}, {name}
          </p>
        </div>
        <Link
          to="/dashboard/statistik"
          className="inline-flex h-9 items-center justify-center gap-2 self-start rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-900 shadow-sm hover:bg-zinc-50"
        >
          <BarChart2 className="size-4" />
          Statistiken
        </Link>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon
          return (
            <section key={card.label} className="rounded-xl border border-zinc-200 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-2">
                <p className="text-sm font-medium text-zinc-500">{card.label}</p>
                <Icon className="size-4 text-zinc-400" />
              </div>
              {card.loading ? (
                <div className="mx-4 mb-1 h-8 w-16 animate-pulse rounded-md bg-zinc-100" />
              ) : (
                <p className="px-4 text-2xl font-semibold tabular-nums tracking-tight text-zinc-950">{card.value}</p>
              )}
              <p className="px-4 pt-1 pb-4 text-xs text-zinc-500">{card.hint}</p>
            </section>
          )
        })}
      </div>

      <div className="grid gap-4 xl:grid-cols-7">
        <section className="rounded-xl border border-zinc-200 bg-white shadow-sm xl:col-span-4">
          <div className="flex items-center justify-between gap-3 border-b border-zinc-200 px-4 py-3">
            <div>
              <h2 className="text-sm font-medium text-zinc-950">Server</h2>
              <p className="text-xs text-zinc-500">Status der Verwaltungsschnittstelle</p>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium ${
                serverOnline
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : serverOnline === false
                    ? 'border-red-200 bg-red-50 text-red-700'
                    : 'border-zinc-200 bg-zinc-50 text-zinc-500'
              }`}
            >
              <span
                className={`size-1.5 rounded-full ${
                  serverOnline ? 'bg-emerald-500' : serverOnline === false ? 'bg-red-500' : 'bg-zinc-400'
                }`}
              />
              {serverOnline ? 'Online' : serverOnline === false ? 'Offline' : 'Prüfe…'}
            </span>
          </div>
          <dl className="grid sm:grid-cols-2">
            <div className="border-b border-zinc-100 px-4 py-4 sm:border-r sm:border-b-0">
              <dt className="text-xs text-zinc-500">Uptime</dt>
              <dd className="mt-1 text-right text-lg font-semibold tabular-nums text-zinc-950">
                {serverUptime ?? '—'}
              </dd>
            </div>
            <div className="px-4 py-4">
              <dt className="text-xs text-zinc-500">Antwortzeit</dt>
              <dd className="mt-1 text-right text-lg font-semibold tabular-nums text-zinc-950">
                {serverResponseMs != null ? `${serverResponseMs.toLocaleString('de-DE')} ms` : '—'}
              </dd>
            </div>
          </dl>
        </section>

        <section className="rounded-xl border border-zinc-200 bg-white shadow-sm xl:col-span-3">
          <div className="border-b border-zinc-200 px-4 py-3">
            <h2 className="text-sm font-medium text-zinc-950">Schnellzugriff</h2>
            <p className="text-xs text-zinc-500">Häufige Aufgaben</p>
          </div>
          <ul className="divide-y divide-zinc-100">
            {shortcuts.map((item) => {
              const Icon = item.icon
              return (
                <li key={item.to}>
                  <Link to={item.to} className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-zinc-600">
                      <Icon className="size-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-zinc-950">{item.label}</span>
                      <span className="block truncate text-xs text-zinc-500">{item.detail}</span>
                    </span>
                    <ArrowUpRight className="size-4 shrink-0 text-zinc-400" />
                  </Link>
                </li>
              )
            })}
          </ul>
        </section>
      </div>
    </div>
  )
}
